import { useState } from "react";
import { PaperAirplaneIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { Button, ConfirmModal, InfoBox, Property, Tag, Textarea } from "../ui";
import { abroadAdminApi } from "../../api/abroadLoans";
import { errorMessageOf } from "../../api/scholarships";
import { canManageScholarships } from "../scholarships/access";
import { formatKigaliDate } from "../scholarships/model";
import ReadinessChecklist from "../calls/ReadinessChecklist";
import { callPhaseOf, useCallSaved } from "./model";

// What publishing needs (AbroadCalls.publish): a programme, criteria weighing 100 for each, and a closing date ahead
const checksOf = (call) => {
  const programmes = call.programmes ?? [];
  const unweighted = programmes.filter((p) => (p.criteria ?? []).reduce((sum, c) => sum + c.weight, 0) !== 100);
  return [
    { label: "At least one programme is offered", ok: programmes.length > 0 },
    {
      label: unweighted.length ? `Criteria weigh 100 for each programme (not yet: ${unweighted.map((p) => p.programme.name).join(", ")})` : "Criteria weigh 100 for each programme",
      ok: programmes.length > 0 && unweighted.length === 0,
    },
    { label: "The closing date is still ahead", ok: new Date(call.closesAt) > new Date() },
  ];
};

// A loan call's state: the HEC Admin publishes a complete draft, or cancels a call with a reason
export default function StatusTab({ call }) {
  const saved = useCallSaved();
  const [confirming, setConfirming] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const manage = canManageScholarships();
  const phase = callPhaseOf(call);
  const checks = checksOf(call);
  const ready = checks.every((c) => c.ok);

  const run = async (action) => {
    setBusy(true);
    try {
      const { data } = action === "publish" ? await abroadAdminApi.publish(call.id) : await abroadAdminApi.cancel(call.id, reason.trim());
      saved(data);
      toast.success(action === "publish" ? "Loan call published" : "Loan call cancelled");
      setConfirming(null);
      setReason("");
    } catch (error) {
      console.error(`Failed to ${action} loan call ${call.id}:`, error);
      toast.error(errorMessageOf(error, `Failed to ${action} the loan call. Please try again.`));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <div>
        <Property label="State" value={<Tag color={phase.color}>{phase.label}</Tag>} />
        <Property label="Applications open" value={formatKigaliDate(call.opensAt)} />
        <Property label="Applications close" value={formatKigaliDate(call.closesAt)} />
        {call.publishedAt && <Property label="Published" value={formatKigaliDate(call.publishedAt)} />}
        {call.cancelledAt && <Property label="Cancelled" value={formatKigaliDate(call.cancelledAt)} />}
        {call.cancelReason && <Property label="Reason" value={call.cancelReason} />}
      </div>

      {call.state === "DRAFT" && <ReadinessChecklist checks={checks} />}

      {manage && call.state !== "CANCELLED" && (
        <div className="flex flex-col-reverse justify-end gap-3 border-t border-slate-100 pt-5 sm:flex-row">
          <Button variant="outline" icon={<XCircleIcon className="h-4 w-4" />} onClick={() => setConfirming("cancel")}>
            Cancel call
          </Button>
          {call.state === "DRAFT" && (
            <Button icon={<PaperAirplaneIcon className="h-4 w-4" />} disabled={!ready} onClick={() => setConfirming("publish")}>
              Publish
            </Button>
          )}
        </div>
      )}
      {manage && call.state === "DRAFT" && !ready && <InfoBox>Complete the points above to publish. Save any changes on the Programmes tab first.</InfoBox>}

      <ConfirmModal
        open={confirming === "publish"}
        onClose={() => setConfirming(null)}
        onConfirm={() => run("publish")}
        loading={busy}
        title="Publish this loan call?"
        description="Students can apply once it opens. Its programmes, documents, rules and criteria can no longer change."
        confirmLabel="Publish"
      />
      <ConfirmModal
        open={confirming === "cancel"}
        onClose={() => setConfirming(null)}
        onConfirm={() => (reason.trim() ? run("cancel") : toast.error("Give a reason for cancelling"))}
        loading={busy}
        destructive
        title="Cancel this loan call?"
        description="Students can no longer see or apply to it. This cannot be undone."
        confirmLabel="Cancel call"
        cancelLabel="Keep it"
      >
        <Textarea label="Reason" required rows={3} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} />
      </ConfirmModal>
    </div>
  );
}
